type LogLevel = 'debug' | 'info' | 'warn' | 'error'

interface LogContext {
  [key: string]: unknown
}

function formatLog(level: LogLevel, scope: string, message: string, context?: LogContext): string {
  const timestamp = new Date().toISOString()
  const base = `[${timestamp}] [${level.toUpperCase()}] [${scope}] ${message}`
  if (context && Object.keys(context).length > 0) {
    return `${base} ${JSON.stringify(context)}`
  }
  return base
}

// Note: this logger deliberately does NOT auto-forward error-level logs to
// the telemetry backend. Forwarding here would require a dynamic import of
// `lib/telemetry` (server-only) from what is an isomorphic module, which
// bundlers end up tracing into the client chunk and hard-failing the build.
//
// The pattern we've adopted instead:
//   - Catch blocks in API routes and server components call
//     `captureException(err, ctx)` from `@/lib/telemetry` explicitly —
//     that's where the real stack + context lives anyway.
//   - Client error boundaries call `captureException` from
//     `@/lib/telemetry-client` explicitly.
//   - `logger.error(...)` stays a local-console-only signal for
//     non-exceptional conditions (authorization denied, rate limited, etc).

function createScopedLogger(scope: string) {
  return {
    debug: (message: string, context?: LogContext) => {
      if (process.env.NODE_ENV === 'development') {
        console.debug(formatLog('debug', scope, message, context))
      }
    },
    info: (message: string, context?: LogContext) => {
      console.info(formatLog('info', scope, message, context))
    },
    warn: (message: string, context?: LogContext) => {
      console.warn(formatLog('warn', scope, message, context))
    },
    error: (message: string, context?: LogContext) => {
      console.error(formatLog('error', scope, message, context))
    },
  }
}

export const logger = {
  scope: createScopedLogger,
}
